import { AxiosResponse } from 'axios';
import { bunja } from 'bunja';
import { atom } from 'jotai';
import { atomWithInfiniteQuery } from 'jotai-tanstack-query';
import { atomWithDefault } from 'jotai/utils';

import { getCommunityFeedsBySymbolApi, getCommunityPublicFeedsApi } from '@flipster/apis/v1/community';
import {
  CommunityFeedItemWithSymbol,
  CommunityFeedTabType,
  GetCommunityFeedBySymbolResponse,
  GetCommunityPublicFeedResponse,
} from '@flipster/apis/v1/community/types';
import { authBunja } from '@flipster/features/src/shared/auth/state';
import { SymbolScope } from '@flipster/features/src/shared/symbol/bunja';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { getLocalStorage, setLocalStorage } from '@flipster/utils/localStorage';
import Num from '@flipster/utils/num';

const CommunityFeedCount = 20;
export const CommunityFeedQueryKey = 'getCommunityFeedsBySymbolApi';

export const feedNudgeBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const key = 'flipster.configs.v1.feedTabRedDot.livePnlShoutout';
  const feedNudgeBaseAtom = atomWithDefault(() => getLocalStorage(key) ?? (() => (setLocalStorage(key, true), true))());
  const feedNudgeAtom = atom(
    (get) => get(feedNudgeBaseAtom),
    (_, set) => (setLocalStorage(key, false), set(feedNudgeBaseAtom, false)),
  );
  function removeFeedRedDot() {
    store.set(feedNudgeAtom);
  }
  return { feedNudgeAtom, removeFeedRedDot };
});
feedNudgeBunja.debugLabel = 'feedNudgeBunja';

export const communityFeedBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { isLoggedInAtom } = bunja.use(authBunja);
  const { removeFeedRedDot } = bunja.use(feedNudgeBunja);
  const symbol = bunja.use(SymbolScope);
  const communityFeedTabAtom = atom<CommunityFeedTabType>('POST');
  const communityFeedBaseAtom = atomWithInfiniteQuery((get) => {
    const isLoggedIn = get(isLoggedInAtom);
    const feedType = get(communityFeedTabAtom);
    return {
      queryKey: [CommunityFeedQueryKey, symbol, feedType, isLoggedIn],
      queryFn: ({ pageParam }: { pageParam: string | undefined }) => {
        if (!isLoggedIn) {
          return getCommunityPublicFeedsApi(symbol, { feedType });
        }
        return getCommunityFeedsBySymbolApi(symbol, {
          after: pageParam,
          count: CommunityFeedCount,
          showEligibility: false,
          showAdditionalData: pageParam == null,
          feedType,
        });
      },
      initialPageParam: undefined,
      getNextPageParam: (
        lastPage: AxiosResponse<GetCommunityFeedBySymbolResponse | GetCommunityPublicFeedResponse>,
      ) => {
        if (!isLoggedIn) return;
        if (feedType === 'POST') return lastPage.data.feedItems.at(-1)?.indexedCreatedAt;
        return lastPage.data.feedItems.at(-1)?.feedId; // type === 'TRADE'
      },
      enabled: symbol != null,
      refetchOnMount: true,
    };
  });
  const communityFeedAtom = atom((get): CommunityFeedItemWithSymbol[] | undefined =>
    get(communityFeedBaseAtom).data?.pages.flatMap(
      (page) => page.data.feedItems.map((item) => ({ ...item, symbol })), // TODO: remove after all feed item contains symbol
    ),
  );
  const symbolTopTraderStatAtom = atom((get) => {
    const first = get(communityFeedBaseAtom).data?.pages[0].data;
    return first?.symbolTopTraderStat;
  });
  const symbolOpenPositionPctAtom = atom((get) => {
    const symbolTopTraderStat = get(symbolTopTraderStatAtom);
    if (symbolTopTraderStat?.longPositionPct && symbolTopTraderStat?.shortPositionPct) {
      return {
        longPositionPct: Num(symbolTopTraderStat?.longPositionPct),
        shortPositionPct: Num(symbolTopTraderStat?.shortPositionPct),
        updatedAt: symbolTopTraderStat.updatedAt,
      };
    }
  });
  const symbolOpenPositionsAtom = atom((get) => get(symbolTopTraderStatAtom)?.openPositions ?? []);
  function fetchNextPage() {
    const { hasNextPage, fetchNextPage: fetch } = store.get(communityFeedBaseAtom);
    if (!hasNextPage) return;
    fetch();
  }
  const symbolLeaderboardAtom = atom((get) => {
    const first = get(communityFeedBaseAtom).data?.pages[0].data;
    return first?.symbolLeaderboard;
  });
  const symbolLivePnlAtom = atom((get) => {
    const first = get(communityFeedBaseAtom).data?.pages[0].data;
    return first?.symbolLivePnl;
  });
  const isPendingAtom = atom((get) => get(communityFeedBaseAtom).isPending);

  bunja.effect(() => {
    removeFeedRedDot();
    return () => {};
  });

  return {
    symbol,
    communityFeedTabAtom,
    communityFeedAtom,
    fetchNextPage,
    symbolLeaderboardAtom,
    symbolOpenPositionPctAtom,
    symbolOpenPositionsAtom,
    symbolLivePnlAtom,
    isPendingAtom,
  };
});
communityFeedBunja.debugLabel = 'communityFeedBunja';
